/* global modForm */
import { getNextStepId, getPrevStepId, getConsentListing } from '../..';

/**
 * @param {string|null} attr - `data-show-step` value (single name or comma-separated names)
 * @param {string|null} stepName - Step's `data-step-name`
 * @returns {boolean} Whether any token in `attr` equals `stepName` (after trim).
 */
function dataShowStepTargetsStepName(attr, stepName) {
	if (!attr || !stepName) {
		return false;
	}
	return attr.split(',').some((token) => token.trim() === stepName);
}

/**
 * Steps with no matching data-show-step controls are shown by default.
 * If any radio/checkbox in the form has data-show-step equal to this step's data-step-name
 * (or lists that name among comma-separated values), the step is shown only when at least
 * one of those controls is checked (OR).
 * @param {string} stepId - Step element id (e.g. step-2)
 * @returns {boolean} Whether the step may be shown given data-show-step controls (true if none apply or any is checked).
 */
function isStepVisibleByDataShowStep(stepId) {
	const $form = modForm.opts.form;
	if (!$form || !stepId) {
		return true;
	}
	const $step = $form.querySelector('#' + stepId);
	if (!$step) {
		return true;
	}
	const stepName = $step.getAttribute('data-step-name');
	const $inputs = modForm.opts.conditionalShowStepInputs;
	if (!$inputs || !$inputs.length) {
		return true;
	}

	const triggersForStep = $inputs.filter((input) => {
		const target = input.getAttribute('data-show-step');
		return dataShowStepTargetsStepName(target, stepName);
	});
	if (triggersForStep.length === 0) {
		return true;
	}
	return triggersForStep.some((input) => input.checked);
}

/**
 * Skip steps that are hidden by data-show-step until a visible step is reached.
 * @param {string} oldStepId - Previously active step id
 * @param {string} stepId - Requested step id
 * @returns {string|undefined} Resolved step id, or undefined if there is no next/prev to skip to
 */
export function showStepConditionally(oldStepId, stepId) {
	if (!stepId) {
		return stepId;
	}

	const navigatingForward = oldStepId === getPrevStepId(stepId);
	// Skip consecutive hidden steps in one navigation (each sibling may also fail data-show-step).
	while (stepId && !isStepVisibleByDataShowStep(stepId)) {
		const $skippedStep = modForm.opts.form.querySelector('#' + stepId);
		if ($skippedStep) {
			$skippedStep.classList.add('skipped');
			if (modForm.opts.fetchAndFormatConsentListings && (modForm.opts.steps.length - Array.from(modForm.opts.steps).indexOf((0, $skippedStep))) === 2) {
				getConsentListing(modForm.opts.getConsentListingCallback);
			}
		}
		const skipToStepId = navigatingForward ? getNextStepId(stepId) : getPrevStepId(stepId);
		if (!skipToStepId) {
			return undefined;
		}
		stepId = skipToStepId;
	}
	const $target =  modForm.opts.form.querySelector('#' + stepId);
	if ($target && $target.classList.contains('skipped')) {
		$target.classList.remove('skipped');
	}
	return stepId;
}
